import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  Input,
  Text,
  Flex,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import MultiSelect from '../../../../reports/components/MultiSelect'
import EmailInput from '../../../../reports/pages/components/EmailInput'
import { toast } from 'react-toastify'
import { useGetDepartmentsByTenantQuery, useInviteUserMutation } from '../../../api/api'
import SingleSelect from '../../../../reports/components/SingleSelect'

export default function InviteUserModal({ isOpen, onClose, locations, user }) {
  const { handleSubmit, register, reset, setValue, watch } = useForm()
  const [emails, setEmails] = useState([])
  const [inviteUser, { isLoading: isInviteUserLoading }] = useInviteUserMutation()
  const { data } = useGetDepartmentsByTenantQuery({ tenant_id: user.tenant.id })

  const selectedLocations = watch('selectedLocations') || []
  const selectedDepartment = watch('selectedDepartment') || null

  const filteredLocations =
    locations?.results?.filter((loc) => {
      if (!selectedDepartment) return true
      return loc.department === selectedDepartment
    }) || []

  const locationSelect = filteredLocations.map((x) => {
    return { id: x.id, name: x.title }
  })

  const departmentSelect = data?.results?.map((x) => {
    return { id: x.id, name: x.name }
  })

  const setSelectedLocations = (selectedLocations: string[]) => {
    setValue('selectedLocations', selectedLocations)
  }

  const setSelectedDepartment = (selectedDepartment: string) => {
    setValue('selectedDepartment', selectedDepartment)
  }

  const onCloseModal = () => {
    setEmails([])
    onClose()
  }

  const onSubmit = async () => {
    const toastId = toast.info('Sending invitation...', {
      style: { padding: '10px 20px' },
      autoClose: false,
      isLoading: true,
      progress: undefined,
    })
    try {
      await inviteUser({
        name: watch('name'),
        email: emails,
        locations: watch('selectedLocations'),
        department: selectedDepartment,
      }).unwrap()
      toast.update(toastId, {
        render: 'Invitation sent successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      })
      reset()
      onCloseModal()
    } catch {
      toast.update(toastId, {
        render: `Failed to invite user`,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    }
  }

  const disabled = () => {
    return emails.length === 0 || !watch('name') || !watch('selectedLocations')
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset()
        onCloseModal()
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex direction="column">
            <Text>Invite user</Text>
            <Text fontSize="12px" color="#9B9B9B">
              An invitation will be sent via email
            </Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody display="flex" flexDirection="column" gap={4}>
            <FormControl>
              <Text>Name</Text>
              <Input {...register('name', { required: true })} placeholder="Name" />
            </FormControl>

            <FormControl>
              <Text>Email</Text>
              <EmailInput emails={emails} setEmails={setEmails} singleEmail />
            </FormControl>

            <FormControl>
              <Text>Department</Text>
              <SingleSelect
                key="department"
                items={departmentSelect}
                isDisabled={false}
                {...register('selectedDepartment')}
                setSelectedOption={setSelectedDepartment}
                selectedOption={selectedDepartment}
                header="Select department type"
              />
            </FormControl>

            <FormControl>
              <Text>Location/s</Text>
              <MultiSelect
                items={locationSelect}
                isDisabled={!selectedDepartment}
                setSelectedOptions={setSelectedLocations}
                selectedOptions={selectedLocations}
                setValue={setValue}
                header={`Select location/s${
                  selectedLocations.length > 0 ? ` (${selectedLocations.length})` : ''
                }`}
                tooltip={{
                  label: 'Select Department first!',
                  hasArrow: true,
                  placement: 'top',
                }}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                reset()
                onClose()
              }}
            >
              Close
            </Button>
            <Button
              isDisabled={disabled()}
              isLoading={isInviteUserLoading}
              colorScheme="blue"
              type="submit"
            >
              Invite
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
